import { Box, Typography } from '@mui/material';
import { StoryPageLayout } from '../layouts';

/**
 * Gen AI for Non-Profit Modernization Assessment
 *
 * This page showcases a business outcome story for non-profit AI modernization.
 * The STORY is the primary feature, demonstrating value through customer
 * success narrative. The form sidebar provides easy access for interested
 * prospects to request their own assessment.
 *
 * STRUCTURE:
 * - Dark header with page title
 * - Two-column layout (story content 2/3 left, form 1/3 right sticky)
 * - Customer overview section (enhanced with green accent)
 * - Business driver section (enhanced with green accent)
 * - Assessment approach section (enhanced with green accent)
 * - Business outcome section (enhanced with green accent)
 * - Form sidebar with sticky positioning (desktop) or stacked below (mobile)
 *
 * @returns {import('react').ReactElement} Gen AI for Non-Profit business outcome page
 */
function GenAINonProfit() {
  return (
    <>
      {/*
       * Hidden at the client's request (Sep 2026) while this business outcome
       * and the Amazon Connect nonprofit case study are refreshed into a single
       * page. The route stays live so links already sent by email and posted on
       * LinkedIn still resolve; only discovery is switched off — this tag, the
       * sitemap entry, and `draft: true` on the update tile.
       * Remove all three when the merged case study ships.
       *
       * Rendered bare rather than inside <Helmet>: this app is on React 19 and
       * `react-helmet` 6.1.0 no-ops there, so nothing in a <Helmet> block
       * reaches the document. React 19 hoists <meta> to <head> natively.
       */}
      <meta name="robots" content="noindex, follow" />

      <StoryPageLayout
        title="AI for a Cause: A Nonprofit Modernization journey"
        subtitle="Empowering Youth Services with AWS AI & Communication Solutions"
        formHeading="Request Your Modernization Assessment"
        formFilePath="/forms/genai-nonprofit-form.txt"
        heroImage="/images/nonprofit-ai-modernization-hero.jpg"
        heroImageAlt="Nonprofit AI modernization journey - empowering youth services with AWS cloud solutions and AI technology"
        errorMessage="Unable to load assessment form"
        loadingMessage="Loading assessment form..."
      >
        {/* Customer Section */}
        <Box
          sx={{
            mb: { xs: 6, md: 10 },
            borderLeft: {
              xs: '4px solid var(--color-primary)',
              md: '6px solid var(--color-primary)',
            },
            pl: { xs: 3, md: 4 },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 3,
              color: 'var(--color-heading)',
              fontSize: { xs: '1.75rem', md: '2rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Customer
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.0625rem',
              lineHeight: 1.75,
              color: 'var(--color-text)',
              fontWeight: 400,
            }}
          >
            A California-based nonprofit dedicated to empowering homeless youth
            sought to modernize its communications and improve staff
            productivity through technology. With over 170 beds across multiple
            facilities, a range of job training programs, and an active
            volunteer and donor network, the organization needed scalable tools
            to coordinate teams and strengthen community engagement. Facing
            limited staff capacity, leadership turned to RSM Collective to
            explore how AI could enhance communication, automate routine
            processes, and expand its impact.
          </Typography>
        </Box>

        {/* Visual separator - R logo */}
        <Box
          sx={{
            textAlign: 'center',
            my: 6,
          }}
        >
          <Box
            component="img"
            src="/images/r-logo.png"
            alt="Section separator"
            sx={{
              width: '40px',
              height: 'auto',
              opacity: 0.7,
            }}
          />
        </Box>

        {/* Business Driver Section */}
        <Box
          sx={{
            mb: { xs: 6, md: 10 },
            borderLeft: {
              xs: '4px solid var(--color-primary)',
              md: '6px solid var(--color-primary)',
            },
            pl: { xs: 3, md: 4 },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 3,
              color: 'var(--color-heading)',
              fontSize: { xs: '1.75rem', md: '2rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Business Driver
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.0625rem',
              lineHeight: 1.75,
              color: 'var(--color-text)',
              fontWeight: 400,
            }}
          >
            As the organization expanded its programs and community
            partnerships, leadership recognized the need to move beyond manual
            communication workflows and disconnected data systems. With the busy
            holiday season approaching, the immediate priority was to deploy a
            call management system that ensured every donor, volunteer, and
            youth inquiry received timely attention. In parallel, the team
            sought to modernize its grant response process, streamline marketing
            outreach, and improve youth intake coordination through technology.
          </Typography>
        </Box>

        {/* Visual separator - R logo */}
        <Box
          sx={{
            textAlign: 'center',
            my: 6,
          }}
        >
          <Box
            component="img"
            src="/images/r-logo.png"
            alt="Section separator"
            sx={{
              width: '40px',
              height: 'auto',
              opacity: 0.7,
            }}
          />
        </Box>

        {/* Assessment Approach Section */}
        <Box
          sx={{
            mb: { xs: 6, md: 10 },
            borderLeft: {
              xs: '4px solid var(--color-primary)',
              md: '6px solid var(--color-primary)',
            },
            pl: { xs: 3, md: 4 },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 3,
              color: 'var(--color-heading)',
              fontSize: { xs: '1.75rem', md: '2rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Assessment Approach
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.0625rem',
              lineHeight: 1.75,
              color: 'var(--color-text)',
              mb: 3,
              fontWeight: 400,
            }}
          >
            RSM Collective's unique model combines our AWS Modernization
            Assessment framework, specialized technology partners, and AWS
            service expertise into a single delivery ecosystem called The
            Collective. This integrated approach ensures every engagement moves
            from strategy to execution with consistency, accountability, and
            measurable outcomes.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.0625rem',
              lineHeight: 1.75,
              color: 'var(--color-text)',
              fontWeight: 400,
            }}
          >
            For this assessment, RSM Collective conducted a series of workshops
            to review the organization's current call management, grant
            discovery, and response processes, along with its early use of AI
            tools. RSM Collective identified opportunities to integrate AWS
            services and open-source technologies that support the nonprofit's
            mission and enable data-driven program growth.
          </Typography>
        </Box>

        {/* Visual separator - R logo */}
        <Box
          sx={{
            textAlign: 'center',
            my: 6,
          }}
        >
          <Box
            component="img"
            src="/images/r-logo.png"
            alt="Section separator"
            sx={{
              width: '40px',
              height: 'auto',
              opacity: 0.7,
            }}
          />
        </Box>

        {/* Business Outcome Section */}
        <Box
          sx={{
            mb: { xs: 6, md: 10 },
            borderLeft: {
              xs: '4px solid var(--color-primary)',
              md: '6px solid var(--color-primary)',
            },
            pl: { xs: 3, md: 4 },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 3,
              color: 'var(--color-heading)',
              fontSize: { xs: '1.75rem', md: '2rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Business Outcome
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.0625rem',
              lineHeight: 1.75,
              color: 'var(--color-text)',
              mb: 3,
              fontWeight: 400,
            }}
          >
            The RSM Collective Modernization Assessment delivered a targeted
            data and AI modernization strategy that aligned cloud architecture
            with the organization's mission to empower homeless youth through
            technology.
          </Typography>
          <Box
            component="ul"
            sx={{
              pl: 0,
              listStyle: 'none',
              '& li': {
                fontSize: '1.0625rem',
                lineHeight: 1.75,
                color: 'var(--color-text)',
                mb: 2,
                pl: 4,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '0.6em',
                  width: '8px',
                  height: '8px',
                  backgroundColor: 'var(--color-primary)',
                  borderRadius: '50%',
                },
                '& strong': {
                  color: 'var(--color-heading)',
                  fontWeight: 600,
                },
              },
            }}
          >
            <li>
              <strong>Modernized Communications:</strong> Deploy an AWS-based
              contact center using Amazon Connect and Contact Lens to
              intelligently route and transcribe every call for under $300 per
              month, improving response time and engagement insights.
            </li>
            <li>
              <strong>AI-Powered Operations:</strong> Introduce a branded,
              private Bedrock-based GenAI environment to align nonprofit LLM
              workflows across fundraising, outreach, grant writing, and case
              management— optimizing each activity with the most suitable model.
            </li>
            <li>
              <strong>Governed AI Adoption:</strong> Establish policy-driven
              GenAI governance aligned with security and privacy best practices
              to ensure responsible, compliant AI use.
            </li>
            <li>
              <strong>Operational Efficiency:</strong> Automate repetitive
              communication and documentation tasks, reducing staff workload and
              enabling more focus on youth programs and community impact.
            </li>
            <li>
              <strong>Cloud Economics:</strong> Leverage AWS nonprofit credits
              and pay-as-you-go pricing to sustain scalability within budget
              constraints while onboarding to the RSM Collective FinOps platform
              for ongoing cost visibility and optimization.
            </li>
          </Box>
        </Box>
      </StoryPageLayout>
    </>
  );
}

export default GenAINonProfit;
